import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, User, LogOut, Settings } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const Navbar = () => {
  const { user, login, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200 shadow-sm transition-all glass-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-9 h-9 bg-[#FF5A00] rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </div>
            <span className="font-outfit font-bold text-xl text-slate-900 hidden sm:block">Privacy Fotos</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-slate-600 hover:text-[#FF5A00] transition-colors font-medium"
              data-testid="nav-home"
            >
              Início
            </Link>
            <Link 
              to="/#top-rated" 
              className="text-slate-600 hover:text-[#FF5A00] transition-colors font-medium"
              data-testid="nav-top-rated"
            >
              Melhores Avaliados
            </Link>
            <Link 
              to="/#highlights" 
              className="text-slate-600 hover:text-[#FF5A00] transition-colors font-medium"
              data-testid="nav-highlights"
            >
              Destaques
            </Link>
          </nav>

          {/* Auth Section */}
          <div className="flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 focus:outline-none" data-testid="user-menu-trigger">
                    <Avatar className="h-9 w-9 border-2 border-[#FF5A00]/20">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-[#FF5A00] text-white text-sm">
                        {user.name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium text-slate-700">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-slate-900">{user.name}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {user.is_admin && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                        <Settings className="mr-2 h-4 w-4" />
                        Painel Admin
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <button
                onClick={login}
                className="bg-[#FF5A00] text-white font-semibold px-5 py-2.5 rounded-full hover:bg-[#E04F00] transition-colors cta-button"
                data-testid="login-button"
              >
                Entrar com Google
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-slate-600 hover:text-[#FF5A00]"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <nav className="flex flex-col gap-3">
              <Link 
                to="/" 
                className="text-slate-600 hover:text-[#FF5A00] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Início
              </Link>
              <Link 
                to="/#top-rated" 
                className="text-slate-600 hover:text-[#FF5A00] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Melhores Avaliados
              </Link>
              <Link 
                to="/#highlights" 
                className="text-slate-600 hover:text-[#FF5A00] transition-colors font-medium py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                Destaques
              </Link>
              {user?.is_admin && (
                <Link 
                  to="/admin" 
                  className="text-slate-600 hover:text-[#FF5A00] transition-colors font-medium py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Painel Admin
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
